/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import './UnlockWalletForm.scss';

import * as React from 'react';

import {
    TextBlock,
    Icon,
    Heading,
    Button,
    Alert,
    Select,
    FormValidationAlert,
    TextFieldStateless,
} from 'sarsaparilla';

import { useUnlockChallengeMutation } from '../../api/queries';
import type { CardBrand } from '../../api/digital-wallet-types';
import { formatCreditCardList } from './SavedCreditCardCard.utils';
import { months, years } from './NewCreditCardForm.utils';

interface UnlockWalletFormProps {
    cardCount?: number;
    isLockedExternal?: boolean;
    brands?: Array<CardBrand>;
    remainingAttempts?: number;
    internalAccountID?: string;
    accountID?: string;
    onSuccess?: any;
}

type ErrorItem = {
    inputId: string;
    errorText: string;
};

export function UnlockWalletForm({
    cardCount,
    brands,
    isLockedExternal,
    remainingAttempts = 10,
    internalAccountID,
    accountID,
    onSuccess,
}: UnlockWalletFormProps) {
    const [validationErrors, setValidationErrors] = React.useState<ErrorItem[] | null>(
        null
    );

    const { mutate, isError, isPending, reset } = useUnlockChallengeMutation({
        onSuccess,
    });

    const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        reset(); // Reset any previous errors
        setValidationErrors(null); // Reset validation

        const formData = new FormData(event.currentTarget);
        const formErrors: ErrorItem[] = [];

        // Get last 4 digits and validate
        const lastFour = formData.get('last4') as string;

        if (!lastFour || lastFour.length !== 4 || !/^\d{4}$/.test(lastFour)) {
            formErrors.push({
                inputId: 'last4',
                errorText: 'Last 4 Digits must be 4 numbers.',
            });
        }

        let expiration: string | null = null;
        let internal_account_id: string | null = null;
        let account_id: string | null = null;

        if (internalAccountID) {
            internal_account_id = internalAccountID;
        }

        if (accountID) {
            account_id = accountID;
        }

        if (isLockedExternal) {
            const month = formData.get('month') as string;
            const year = formData.get('year') as string;

            // Validate month and year
            if (!month) {
                formErrors.push({
                    inputId: 'month',
                    errorText: 'Month is required.',
                });
            }

            if (!year) {
                formErrors.push({
                    inputId: 'year',
                    errorText: 'Year is required.',
                });
            }

            // Format expiration date as MMYY
            expiration = `${month}${year}`;
        }

        if (formErrors.length > 0) {
            setValidationErrors(formErrors);
            return;
        }

        // Make API call here
        mutate({
            lastFour,
            expiration,
            internal_account_id,
            account_id,
        });
    };

    let cardBlurb = (
        <p>
            There are {cardCount} saved cards {formatCreditCardList(brands)}. To unlock
            your digital wallet, enter the last four digits{' '}
            {isLockedExternal ? 'and expiration date' : ''} of any of these cards.
        </p>
    );

    if (cardCount === 1) {
        cardBlurb = (
            <p>
                There is one saved card {formatCreditCardList(brands)}. To unlock your
                digital wallet, enter the last four digits{' '}
                {isLockedExternal ? 'and expiration date' : ''} of that card.
            </p>
        );
    }

    return (
        <>
            {isError ? (
                <Alert type="error" shouldFocusOnMount className="mb-3">
                    An error occurred. Please try again.
                </Alert>
            ) : null}

            {validationErrors ? (
                <FormValidationAlert className="mb-3" errors={validationErrors} />
            ) : null}

            <form noValidate className="cart--csr-challenge-form" onSubmit={onSubmit}>
                <Icon iconName="lock" size="xl" className="lock-icon" />

                <div>
                    <Heading headingLevel={3} appearance="h5" className="mb-1">
                        Digital Wallet is Locked
                    </Heading>
                    <TextBlock width="md" className="mb-3">
                        {isLockedExternal ? (
                            <p>
                                Due to recent account or password changes, we locked your
                                digital wallet for your protection.
                            </p>
                        ) : null}

                        {isLockedExternal && remainingAttempts === 0 ? (
                            <>
                                <p>
                                    <strong>
                                        This wallet is locked from further unlock attempts
                                        for 24 hours.
                                    </strong>
                                </p>
                                <p>
                                    If you are having trouble unlocking your digital
                                    wallet, please reach out to the contact center to
                                    reset your digital wallet capability.
                                </p>
                            </>
                        ) : (
                            cardBlurb
                        )}

                        {isLockedExternal &&
                        remainingAttempts <= 3 &&
                        remainingAttempts > 0 ? (
                            <p>
                                You have{' '}
                                <strong>{remainingAttempts} remaining attempts</strong>,
                                after which this wallet will be locked for 24 hours.
                            </p>
                        ) : null}
                    </TextBlock>

                    {isLockedExternal && remainingAttempts === 0 ? null : (
                        <div className="form-elements mt-4">
                            <TextFieldStateless
                                id="last4"
                                label={
                                    cardCount === 1
                                        ? 'Last 4 Digits'
                                        : 'Last 4 Digits of Any Saved Card'
                                }
                                name="last4"
                                type="text"
                                autoComplete="off"
                                maxLength={4}
                                isRequired
                                isInvalid={
                                    !!validationErrors?.find((e) => e.inputId === 'last4')
                                }
                                errorText={
                                    validationErrors?.find((e) => e.inputId === 'last4')
                                        ?.errorText
                                }
                            />
                            {isLockedExternal ? (
                                <>
                                    <Select
                                        label="Month"
                                        isRequired
                                        id="month"
                                        name="month"
                                        placeholder="MM"
                                        options={months.map((m) => ({
                                            label: m,
                                            value: m,
                                        }))}
                                        isInvalid={
                                            !!validationErrors?.find(
                                                (e) => e.inputId === 'month'
                                            )
                                        }
                                    />
                                    <Select
                                        label="Year"
                                        isRequired
                                        id="year"
                                        name="year"
                                        placeholder="YYYY"
                                        options={years.map((y) => ({
                                            label: `20${y}`,
                                            value: y,
                                        }))}
                                        isInvalid={
                                            !!validationErrors?.find(
                                                (e) => e.inputId === 'year'
                                            )
                                        }
                                    />
                                </>
                            ) : null}

                            <Button
                                type="submit"
                                appearance="tertiary-alt"
                                className="submit-button"
                                isLoading={isPending}
                            >
                                Unlock
                            </Button>
                        </div>
                    )}
                </div>
            </form>
        </>
    );
}

// cSpell:ignore MMYY
